import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Form, Formik} from 'formik';
import {getEmployeeSatisfactionStatuses, getEmployeeStuckItems} from '../../../../../../api/tasks';
import Question from './Question';
import Spinner from '../../../../../common/Spinner';
import {TaskField} from '../../../../../../constants/tasks';
import {CurrentTaskAnswer, EmployeeSatisfactionStatuses, EmployeeStuckItem, TaskFormField} from '../../../../types';
import {getInitialValues} from './helpers/getInitialValues';
import {getValidationSchema} from './helpers/getValidationSchema';
import {Button} from '../../../../../common/Button';
import EnsurePermission from '../../../../../common/EnsurePermission';
import permissionsInfo from '../../../../../../constants/permissionsInfo';
import {useSnackbarHook} from '../../../../../../hooks/useSnackbar';

type QuestionnaireItems = {
  stuckItems: Nullable<EmployeeStuckItem[]>,
  satisfactionStatuses: Nullable<EmployeeSatisfactionStatuses[]>,
}

type EmployeeSurveyQuestionnaireProps = {
  formFields: Nullable<TaskFormField[]>,
  answers: CurrentTaskAnswer[],
  submitTask: (values: Dictionary<string | string[]>) => void,
  isCurrentUserAssignee: boolean,
  isRunning: boolean,
  isSubmitting: boolean,
}

const EmployeeSurveyQuestionnaire: FC<EmployeeSurveyQuestionnaireProps> = ({
  formFields = [], answers, submitTask,
  isCurrentUserAssignee, isRunning, isSubmitting
}) => {
  const {t} = useTranslation(['tasks', 'common']);
  const [isLoading, setIsLoading] = useState(false);
  const [
    questionnaireItems,
    setQuestionnaireItems
  ] = useState<Nullable<QuestionnaireItems>>(null);
  const {addErrorToast} = useSnackbarHook();

  const getFieldsData = useCallback(async () => {
    if (!formFields?.length) {
      return;
    }

    setIsLoading(true);

    const stuckItemsField = formFields?.find(({type}) => {
      return type === TaskField.EMPLOYEE_STUCKS_CHECKLIST;
    });

    const sentimentSliderField = formFields?.find(({type}) => {
      return type === TaskField.SENTIMENT_SLIDER;
    });

    try {
      const stuckItemPromise = stuckItemsField ?
        getEmployeeStuckItems() : null;

      const satisfactionPromise = sentimentSliderField ?
        getEmployeeSatisfactionStatuses() : null;

      const [stuckItems, satisfactionStatuses] = await Promise.all([
        stuckItemPromise,
        satisfactionPromise
      ]);

      setQuestionnaireItems({
        stuckItems,
        satisfactionStatuses
      });
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsLoading(false);
    }
  }, [formFields]);

  useEffect(() => {
    getFieldsData();
  }, []);

  const initialValues = useMemo(() =>
    getInitialValues(formFields, answers),
  [formFields, answers]
  );
  const validationSchema = useMemo(() =>
    getValidationSchema(formFields),
  [formFields]
  );

  if (!formFields?.length) {
    return null;
  }

  if (isLoading) {
    return <Spinner isLoading={isLoading} />;
  }

  const {stuckItems = [], satisfactionStatuses = []} = questionnaireItems || {};

  const isDisabled = !isCurrentUserAssignee ||
    !isRunning ||
    isSubmitting;

  return (
    <>
      <h4 className="questionnaire__title">
        {t('employeeSurvey.questionnaire.title')}
      </h4>

      <Formik
        onSubmit={submitTask}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({isValid}) => (
          <Form>
            {formFields?.map((field, id) => (
              <Question
                key={id.toString()}
                field={field}
                disabled={isDisabled}
                stuckItems={stuckItems}
                satisfactionStatuses={satisfactionStatuses}
              />
            ))}

            {isCurrentUserAssignee && isRunning && (
              <EnsurePermission
                elementPermissions={permissionsInfo.tasks.complete}
              >
                <div className="current-task__button-container">
                  <Button
                    label={t('common:complete')}
                    type="submit"
                    disabled={!isValid}
                  />
                </div>
              </EnsurePermission>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default EmployeeSurveyQuestionnaire;
